import { useMemo, useState } from "react";
import { LanderV2MarketingProps, LanderV2StepProps } from "../../../Campaigns/Schemas/LanderV2/types";
import { IClientData } from "../../../../models/ClientData";
import { IQuote } from "../../../../models/QuoteData";
import { formatCurrency, getProviderLogo } from "../../../../modules/common/quotes/quoteUtils";
import { SurveyJSForm } from "../../../../modules/surveyjs_forms/SurveyJSForm";
import { lv2Theme } from "../../../../modules/surveyjs_forms/surveyTheme";
import { variantBAssets } from "../../../Utils/variantBAssets";
import { TrustpilotReviewsCarousel } from "../TrustpilotReviewsCarousel";
import { BookingStepBase } from "../annuitycalculator-v1/VariantV1";
import { QuoteExplainerDrawer, QuoteExplainerType } from "../shared/QuoteExplainerDrawer";
import { QuoteAdviceActions } from "../shared/QuoteAdviceActions";
import { ComplianceNote } from "../shared/ComplianceNote";
import { QuoteBoostBand } from "../shared/QuoteBoostBand";
import { LanderV2LoadingOverlay } from "../LanderV2LoadingOverlay";
import { PartnerAssociationBlock } from "../../../../modules/common/menus/PartnerAssociationBlock";

const tickGreenRibbedSrc = "/media/misc/tick_green_ribbed.png";
const crossRedRibbedSrc = "/media/misc/cross_red_ribbed.png";
const fscsRibbedSrc = "/media/logos/fscs_protected_small_ribbed.png";
const v1CallNumber = "0808 281 2558";
const v1CallHref = "tel:08082812558";

export function DrawdownLoadingOverlay() {
  return (
    <LanderV2LoadingOverlay
      title="Nearly there"
      subtitle="Calculating your drawdown options"
      providerLogos={[]}
    />
  );
}

const drawdownChecks = [
  {
    key: "flexible",
    content: <>Explore <strong>flexible income</strong> options in minutes</>,
  },
  {
    key: "free-advice",
    content: <>Get <strong>FREE</strong> expert advice on drawdown and annuities</>,
  },
  {
    key: "keep-invested",
    content: <>See how long your pension pot could last</>,
  },
  {
    key: "pros-cons",
    content: <>Your advisor will explain the <strong>pros and cons</strong> of drawdown</>,
  },
];

const processSteps = [
  {
    title: "1. Fill in the basics",
    copy: "Tell us about your pension pot, age and retirement plans.",
    image: variantBAssets.processFeedback,
  },
  {
    title: "2. Review your options",
    copy: "See an illustrative drawdown route alongside leading annuity quotes.",
    image: variantBAssets.processProfit,
  },
  {
    title: "3. Get FREE expert advice",
    copy: "Speak with a specialist about the right income route for your retirement.",
    image: variantBAssets.processSchool,
  },
  {
    title: "4. Decide with confidence",
    copy: "Understand the trade-offs before choosing how to use your pension.",
    image: variantBAssets.processRelax,
  },
] as const;

const providerLogoOverrides: Record<string, string> = {
  aviva: variantBAssets.providerAviva,
  "canada life": variantBAssets.providerCanadaLife,
  just: variantBAssets.providerJust,
  "just retirement": variantBAssets.providerJust,
  "legal & general": variantBAssets.providerLegalGeneral,
  "legal and general": variantBAssets.providerLegalGeneral,
  lv: variantBAssets.providerLv,
  "lv=": variantBAssets.providerLv,
  prudential: variantBAssets.providerPrudential,
  "scottish widows": variantBAssets.providerScottishWidows,
  "standard life": variantBAssets.providerStandardLife,
};

function scrollToEntryForm() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function QuoteEntrySurvey({
  id,
  entrySchemaPage,
  surveyProps,
  initialData,
  onQuoteSubmit,
  onFirstInteraction,
}: Pick<LanderV2MarketingProps, "entrySchemaPage" | "surveyProps" | "initialData" | "onQuoteSubmit" | "onFirstInteraction"> & { id: string }) {
  return (
    <SurveyJSForm
      surveyFormId={id}
      pageSchemas={[entrySchemaPage]}
      initialData={initialData}
      clearDraftOnComplete={false}
      surveyProps={surveyProps}
      surveyTheme={lv2Theme}
      wrapperFormCssClass="lv2-survey pa-v1-entry-survey"
      keepFormVisibleOnComplete
      suppressLoader
      onFirstInteraction={onFirstInteraction}
      onComplete={(data) => onQuoteSubmit(data ?? {})}
    />
  );
}

function RatingProof({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? "pa-v1-rating pa-v1-rating-compact" : "pa-v1-rating"}>
      <span>Rated <strong>Excellent</strong></span>
      <span className="pa-v1-rating-stars" aria-label="5 star rating">
        <i>★</i><i>★</i><i>★</i><i>★</i><i>★</i>
      </span>
    </div>
  );
}

function AdviceCard({ onBook }: { onBook: () => void }) {
  return (
    <aside className="pa-v1-advice-card">
      <h2 className="ux2-text-heading">Not sure which is right for you?</h2>
      <div className="pa-v1-advisor-faces" aria-hidden="true">
        <img src={variantBAssets.advisorSmall1} alt="" />
        <img src={variantBAssets.advisorSmall2} alt="" />
        <img src={variantBAssets.advisorSmall3} alt="" />
      </div>
      <p className="ux2-text-body-lg">Our expert Pension Advisors will help on the right product, at the very best rates</p>
      <QuoteAdviceActions
        actionsClassName="pa-v1-advice-actions"
        buttonClassName="pa-v1-button pa-v1-button-magenta ux2-text-button"
        callHref={v1CallHref}
        callNumber={v1CallNumber}
        className="pa-v1-phone-callout"
        onBook={onBook}
      />
    </aside>
  );
}

function getPensionFund(lead: IClientData) {
  const rawValue = lead.annuity?.pensionFund ?? 0;
  const value = typeof rawValue === "string" ? Number(rawValue.replace(/[^\d.]/g, "")) : Number(rawValue);
  return Number.isFinite(value) ? value : 0;
}

function getAge(data: IClientData) {
  const dob = typeof data.dateOfBirth === "string" ? new Date(data.dateOfBirth) : null;
  if (!dob || Number.isNaN(dob.getTime())) return 55;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
  if (!hasBirthdayPassed) age -= 1;
  return age;
}

function getQuoteLogo(providerName?: string) {
  if (!providerName) return null;
  return providerLogoOverrides[providerName.toLowerCase().trim()] ?? getProviderLogo(providerName);
}

function isFixedTermQuote(quote: IQuote) {
  const name = `${quote.productName ?? ""} ${quote.productType ?? ""}`.toLowerCase();
  const termYears = Number(quote.termYears ?? 0);
  return name.includes("fixed") || (termYears > 0 && termYears < 999);
}

function getSortedQuotes(quotes: IQuote[]) {
  return [...quotes].sort((a, b) => Number(b.annuityAmountPA ?? 0) - Number(a.annuityAmountPA ?? 0));
}

function getTopLifetimeQuote(quotes: IQuote[]) {
  return getSortedQuotes(quotes.filter((quote) => !isFixedTermQuote(quote)))[0] ?? getSortedQuotes(quotes)[0] ?? null;
}

const averageLifeExpectancyAge = 85;

function calculatePotAtAge(startAge: number, targetAge: number, startingPot: number, annualIncome: number, returnRate: number) {
  if (!startingPot || !annualIncome || targetAge <= startAge) return Math.max(0, startingPot);
  let balance = startingPot;
  for (let age = startAge; age < targetAge && balance > 0; age += 1) {
    balance = balance * (1 + returnRate) - annualIncome;
  }
  return Math.max(0, balance);
}

function calculateRunOutAge(age: number, pot: number, annualIncome: number, returnRate: number) {
  if (!pot || !annualIncome) return age;
  let balance = pot;
  let years = 0;
  while (balance > 0 && years < 70) {
    balance = balance * (1 + returnRate) - annualIncome;
    years += 1;
  }
  return age + years;
}

function clampAnnualIncome(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(50000, Math.max(0, Math.round(value)));
}

function clampContributionPercent(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(10, Math.max(0, Math.round(value)));
}

function parseAnnualIncome(value: string) {
  return Number(value.replace(/[^\d]/g, ""));
}

function parseContributionPercent(value: string) {
  return Number(value.replace(/[^0-9.]/g, ""));
}

function parseContributionAmount(value: string) {
  return Math.max(0, Math.round(Number(value.replace(/[^\d]/g, "")) || 0));
}

function DrawdownOptionCard({
  pensionFund,
  currentAge,
  onBook,
  onOpenInfo,
}: {
  pensionFund: number;
  currentAge: number;
  onBook: () => void;
  onOpenInfo: () => void;
}) {
  const [annualIncome, setAnnualIncome] = useState(12000);
  const [returnRate, setReturnRate] = useState(0.05);
  const [contributionMode, setContributionMode] = useState<"percent" | "amount">("percent");
  const [contributionPercent, setContributionPercent] = useState(0);
  const [contributionAnnualIncome, setContributionAnnualIncome] = useState(0);
  const [contributionAmount, setContributionAmount] = useState(0);
  const annualContributionFromPercent = Math.round(contributionAnnualIncome * (contributionPercent / 100));
  const annualContribution = contributionMode === "percent" ? annualContributionFromPercent : contributionAmount;
  const adjustedPensionFund = pensionFund + annualContribution;
  const taxFreeCash = adjustedPensionFund * 0.25;
  const remainingPot = adjustedPensionFund * 0.75;
  const runOutAge = calculateRunOutAge(currentAge, remainingPot, annualIncome, returnRate);
  const potAtAverageLifeExpectancy = calculatePotAtAge(
    currentAge,
    averageLifeExpectancyAge,
    remainingPot,
    annualIncome,
    returnRate
  );
  const runsOutBeforeAverageLifeExpectancy = runOutAge < averageLifeExpectancyAge;
  const updateAnnualIncome = (value: number) => setAnnualIncome(clampAnnualIncome(value));

  return (
    <section className="pd-v1-drawdown-card" aria-labelledby="pd-v1-drawdown-title">
      <div className="pd-v1-pot-grid">
        <article><span>Total pension pot</span><strong>{formatCurrency(adjustedPensionFund)}</strong></article>
        <article><span>Tax-free cash (25%)</span><strong>{formatCurrency(taxFreeCash)}</strong></article>
        <article><span>Remaining pension pot</span><strong>{formatCurrency(remainingPot)}</strong></article>
      </div>
      <div className="pd-v1-contribution-card" aria-label="Contribution input">
        <div>
          <span>Contribution</span>
          <div className="pd-v1-contribution-toggle" role="group" aria-label="Contribution type">
            <button
              type="button"
              className={contributionMode === "percent" ? "pd-v1-contribution-selected" : ""}
              onClick={() => setContributionMode("percent")}
            >
              %
            </button>
            <button
              type="button"
              className={contributionMode === "amount" ? "pd-v1-contribution-selected" : ""}
              onClick={() => setContributionMode("amount")}
            >
              £
            </button>
          </div>
        </div>
        {contributionMode === "percent" ? (
          <div className="pd-v1-contribution-percent-fields">
            <label>
              <span>Annual income</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[£0-9,]*"
                value={`£${contributionAnnualIncome.toLocaleString("en-GB")}`}
                onChange={(event) => setContributionAnnualIncome(parseContributionAmount(event.target.value))}
                aria-label="Annual income for contribution percentage"
              />
            </label>
            <label>
              <span>Contribution percentage</span>
              <span className="pd-v1-income-stepper pd-v1-contribution-stepper">
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  value={`${contributionPercent}%`}
                  onChange={(event) => setContributionPercent(clampContributionPercent(parseContributionPercent(event.target.value)))}
                  aria-label="Contribution percentage"
                />
                <span>
                  <button
                    type="button"
                    aria-label="Decrease contribution percentage"
                    onClick={() => setContributionPercent(clampContributionPercent(contributionPercent - 1))}
                  >
                    <i className="bi bi-chevron-down" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    aria-label="Increase contribution percentage"
                    onClick={() => setContributionPercent(clampContributionPercent(contributionPercent + 1))}
                  >
                    <i className="bi bi-chevron-up" aria-hidden="true"></i>
                  </button>
                </span>
              </span>
            </label>
            <small>Annual contribution: {formatCurrency(annualContributionFromPercent)}</small>
          </div>
        ) : (
          <label>
            <span>Contribution amount</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[£0-9,]*"
              value={`£${contributionAmount.toLocaleString("en-GB")}`}
              onChange={(event) => setContributionAmount(parseContributionAmount(event.target.value))}
              aria-label="Contribution amount"
            />
          </label>
        )}
      </div>
      <p className="pd-v1-drawdown-note">
        Assumes full tax-free cash is taken. Paid as 12 equal monthly instalments. Not personalised advice, figures are illustrative only.
      </p>
      <header>
        <h2 id="pd-v1-drawdown-title">Pension Drawdown</h2>
        <button type="button" className="lv2-info-link" onClick={onOpenInfo}>What is drawdown?</button>
      </header>
      <div className="pd-v1-drawdown-panel">
        <h3>Flexible income you can take when you want</h3>
        <label>
          <span>Desired annual income</span>
          <span className="pd-v1-income-stepper">
            <input
              type="text"
              inputMode="numeric"
              pattern="[£0-9,]*"
              value={`£${annualIncome.toLocaleString("en-GB")}`}
              onChange={(event) => updateAnnualIncome(parseAnnualIncome(event.target.value))}
              aria-label="Desired annual income"
            />
            <span>
              <button
                type="button"
                aria-label="Decrease desired annual income by 1000"
                onClick={() => updateAnnualIncome(annualIncome - 1000)}
              >
                <i className="bi bi-dash-lg" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                aria-label="Increase desired annual income by 1000"
                onClick={() => updateAnnualIncome(annualIncome + 1000)}
              >
                <i className="bi bi-plus-lg" aria-hidden="true"></i>
              </button>
            </span>
          </span>
        </label>
        <span className="pd-v1-return-label">Investment return</span>
        <div className="pd-v1-return-options" role="radiogroup" aria-label="Investment return">
          {[
            ["Low", 0.03],
            ["Med", 0.05],
            ["High", 0.08],
          ].map(([label, value]) => (
            <button
              key={label as string}
              type="button"
              className={returnRate === value ? "pd-v1-return-selected" : ""}
              onClick={() => setReturnRate(value as number)}
            >
              <strong>{label}</strong>
              <span>{Math.round((value as number) * 100)}%</span>
            </button>
          ))}
        </div>
        <div className="pd-v1-runout">
          {runsOutBeforeAverageLifeExpectancy ? (
            <strong>Your pot is likely to last to <span>age {runOutAge}</span></strong>
          ) : (
            <strong>Your pot could last to <span>age {averageLifeExpectancyAge}</span> with around <span>{formatCurrency(potAtAverageLifeExpectancy)}</span> left over</strong>
          )}
        </div>
        <p className="pd-v1-runout-hint">
          Try adjusting your investment return or desired income to see how it changes things
        </p>
        <ul>
          <li>
            <span
              className={`pd-v1-panel-tick ${runsOutBeforeAverageLifeExpectancy ? "pd-v1-panel-cross" : ""}`}
              aria-hidden="true"
            >
              <img src={runsOutBeforeAverageLifeExpectancy ? crossRedRibbedSrc : tickGreenRibbedSrc} alt="" />
              <i className={`bi ${runsOutBeforeAverageLifeExpectancy ? "bi-x-lg" : "bi-check-lg"}`}></i>
            </span>
            {runsOutBeforeAverageLifeExpectancy
              ? <>Your pot is likely to last to an age below average life expectancy ({averageLifeExpectancyAge})</>
              : <>Your pot could last beyond average life expectancy ({averageLifeExpectancyAge})</>}
          </li>
          <li>
            <span className="pd-v1-panel-tick" aria-hidden="true">
              <img src={tickGreenRibbedSrc} alt="" />
              <i className="bi bi-check-lg"></i>
            </span>
            You decide the amount and when it's paid
          </li>
          <li>
            <span className="pd-v1-panel-tick" aria-hidden="true">
              <img src={tickGreenRibbedSrc} alt="" />
              <i className="bi bi-check-lg"></i>
            </span>
            Any money left can be passed on to loved ones
          </li>
        </ul>
        <button type="button" className="pd-v1-book-button ux2-text-button" onClick={onBook}>
          <span className="pd-v1-button-faces" aria-hidden="true">
            <img src={variantBAssets.advisorSmall1} alt="" />
            <img src={variantBAssets.advisorSmall2} alt="" />
            <img src={variantBAssets.advisorSmall3} alt="" />
          </span>
          DISCUSS WITH AN ADVISOR
          <i className="bi bi-chevron-right" aria-hidden="true"></i>
        </button>
        <div className="pd-v1-drawdown-proof">
          <img src={fscsRibbedSrc} alt="FSCS protected" />
          <RatingProof compact />
        </div>
      </div>
    </section>
  );
}

function TopLifetimeQuote({
  quote,
  onBook,
  onOpenInfo,
}: {
  quote: IQuote | null;
  onBook: () => void;
  onOpenInfo: () => void;
}) {
  if (!quote) return null;
  const logo = getQuoteLogo(quote.providerName);
  const uplift = Math.max(1, Math.round(Number(quote.annuityAmountPA ?? 0) * 0.064));

  return (
    <section className="pd-v1-top-quote" aria-labelledby="pd-v1-top-annuity">
      <header>
        <h2 id="pd-v1-top-annuity" className="ux2-text-heading">Top Lifetime Annuity Quote</h2>
        <button type="button" className="lv2-info-link" onClick={onOpenInfo}>What is this?</button>
      </header>
      <article>
        <div className="pd-v1-top-quote-main">
          {logo ? <img src={logo} alt={quote.providerName} /> : <strong>{quote.providerName}</strong>}
          <div>
            <strong>{quote.providerName}</strong>
            <span>Single Life, Enhanced, Level</span>
          </div>
        </div>
        <p className="pd-v1-top-quote-amount">{formatCurrency(quote.annuityAmountPA)}</p>
        <div className="pd-v1-top-quote-uplift">
          <span aria-hidden="true" className="pd-v1-top-quote-faces">
            <img src={variantBAssets.advisorSmall1} alt="" />
            <img src={variantBAssets.advisorSmall2} alt="" />
            <img src={variantBAssets.advisorSmall3} alt="" />
          </span>
          Our pension specialists could get you an extra <strong>{formatCurrency(uplift)}/year</strong> on this annuity
        </div>
        <button type="button" className="ux2-text-button" onClick={onBook}>BOOK A FREE APPOINTMENT</button>
      </article>
    </section>
  );
}

export function DrawdownMarketingVariant1(props: LanderV2MarketingProps) {
  return (
    <article className="pa-v1 pd-v1">
      <header className="pa-v1-top">
        <img src={variantBAssets.penseLogo} alt="Pense" />
        <PartnerAssociationBlock className="pa-v1-associated" />
      </header>

      <section className="pa-v1-hero" aria-labelledby="pd-v1-title">
        <div className="pa-v1-hero-copy">
          <h1 id="pd-v1-title">How long could your pension last in drawdown?</h1>
          <ul className="pa-v1-checks">
            {drawdownChecks.map((check) => (
              <li key={check.key}>
                <span className="pa-v1-check-icon" aria-hidden="true">
                  <img src={tickGreenRibbedSrc} alt="" />
                  <i className="bi bi-check-lg"></i>
                </span>
                <span>{check.content}</span>
              </li>
            ))}
          </ul>
          <RatingProof />
        </div>

        <aside className="pa-v1-cta-card" aria-label="Get drawdown options">
          <img className="pa-v1-fscs-seal" src={fscsRibbedSrc} alt="FSCS protected" />
          <h2>
            See your drawdown options in as little as <strong>2 minutes</strong>
          </h2>
          <QuoteEntrySurvey id="drawdowncalculator-v1-quote-entry" {...props} />
          {props.submitError && <p className="pd-v1-entry-error">{props.submitError}</p>}
        </aside>
      </section>

      <section className="pa-vb-section pa-vb-provider-section pd-v1-provider-section" aria-labelledby="pd-v1-providers">
        <h2 id="pd-v1-providers" className="ux2-text-heading">Trusted Investment manager and platform</h2>
        <div className="pd-v1-investment-logos">
          <img src={variantBAssets.providerAviva} alt="Aviva" />
          <img src="/media/logos/Timeline.webp" alt="Timeline" />
        </div>
      </section>

      <section className="pa-vb-process" aria-labelledby="pd-v1-process">
        <h2 id="pd-v1-process" className="ux2-text-heading">Planning pension drawdown has never been easier</h2>
        <div className="pa-vb-process-grid">
          {processSteps.map((step) => (
            <div className="pa-vb-process-step" key={step.title}>
              <img src={step.image} alt="" aria-hidden="true" />
              <div className="pa-vb-process-step-copy">
                <h3>{step.title}</h3>
                <p className="ux2-text-body">{step.copy}</p>
              </div>
            </div>
          ))}
        </div>
        <button className="pa-vb-button ux2-text-button" type="button" onClick={scrollToEntryForm}>GET STARTED</button>
      </section>

      <section className="pa-vb-section pa-vb-reviews" aria-labelledby="pd-v1-reviews">
        <h2 id="pd-v1-reviews" className="ux2-text-heading">We have helped over <span className="pa-v1-blue-text">1,000</span> customers get the most from their pension</h2>
        <TrustpilotReviewsCarousel />
      </section>

      <section className="pa-vb-card-row pa-v1-card-row" aria-label="Safety and drawdown start">
        <div className="pa-vb-safe-card">
          <img src={variantBAssets.fscsProtected} alt="FSCS protected" />
          <div>
            <h2 className="ux2-text-safe-heading">You are in safe hands!</h2>
            <p className="ux2-text-body-lg">We only partner with FCA-regulated advisors, so your retirement planning is backed by the <strong>FSCS protection scheme.</strong></p>
          </div>
        </div>
        <div className="pa-vb-quote-card pa-v1-inline-cta">
          <img src={variantBAssets.onlineSchool} alt="" aria-hidden="true" />
          <div>
            <h2 className="ux2-text-heading">Ready to explore your pension drawdown options?</h2>
            <p className="ux2-text-body-lg">See your options in as little as <strong>2 minutes</strong></p>
            <button className="pa-vb-button ux2-text-button" type="button" onClick={scrollToEntryForm}>GET STARTED</button>
          </div>
        </div>
      </section>

      <section className="pa-vb-talk" aria-labelledby="pd-v1-talk">
        <img src={variantBAssets.talkIllustration} alt="Appointment booking illustration" />
        <div>
          <h2 id="pd-v1-talk" className="ux2-text-heading">Prefer to talk?</h2>
          <p className="ux2-text-body-lg">Speak directly to a specialist pension advisor for <strong>free</strong>, friendly guidance on drawdown, annuities and retirement income.</p>
          <p className="pa-vb-phone ux2-text-phone">
            <span>Call free on</span>
            <a href={v1CallHref}><strong>{v1CallNumber}</strong></a>
            <span>or</span>
            <button type="button" className="ux2-text-button" onClick={scrollToEntryForm}>GET STARTED</button>
          </p>
        </div>
      </section>
      <ComplianceNote />
    </article>
  );
}

export function DrawdownQuoteResultsStep({ lead, quotes, goToStep }: LanderV2StepProps) {
  const [infoDrawerType, setInfoDrawerType] = useState<QuoteExplainerType | null>(null);
  const firstName = lead.firstName?.trim() ?? "";
  const pensionFund = getPensionFund(lead);
  const age = getAge(lead);
  const topLifetime = useMemo(() => getTopLifetimeQuote(quotes), [quotes]);
  const book = () => goToStep("booking");

  return (
    <section className="pd-v1-results">
      <h1>Adjust your income and contributions to see your drawdown options{firstName ? `, ${firstName}` : ""}</h1>
      <div className="pd-v1-primary-row">
        <DrawdownOptionCard
          pensionFund={pensionFund}
          currentAge={age}
          onBook={book}
          onOpenInfo={() => setInfoDrawerType("drawdown")}
        />
        <AdviceCard onBook={book} />
      </div>

      <section className="pa-v1-next-steps pd-v1-next-steps">
        <h2 className="ux2-text-heading">What happens next?</h2>
        <div>
          <article>
            <img src={variantBAssets.processFeedback} alt="" />
            <p><strong>1.</strong> Book your FREE appointment with a specialist Pension Advisor</p>
          </article>
          <article>
            <img src={variantBAssets.processProfit} alt="" />
            <p><strong>2.</strong> Our advisors check your options and help compare drawdown with guaranteed income</p>
          </article>
          <article>
            <img src={variantBAssets.processSchool} alt="" />
            <p><strong>3.</strong> Decide how to take retirement income with clear guidance</p>
          </article>
        </div>
      </section>

      <p className="pd-v1-compare-line">Compare flexible drawdown income against guaranteed income from an annuity</p>

      <div className="pd-v1-results-main">
        <div>
          <TopLifetimeQuote quote={topLifetime} onBook={book} onOpenInfo={() => setInfoDrawerType("lifetime")} />
        </div>
        <div className="pa-v1-review-pullout lv2-quotes-review-card">
          <h3 className="ux2-text-heading">We've helped over <strong>1,000</strong> customers get the most from their pension</h3>
          <TrustpilotReviewsCarousel mode="single" reviewerAvatarPosition="top" />
        </div>
      </div>

      <section className="pd-v1-results-talk" aria-labelledby="pd-v1-results-talk">
        <img src={variantBAssets.talkIllustration} alt="" aria-hidden="true" />
        <div>
          <h2 id="pd-v1-results-talk" className="ux2-text-heading">Prefer to talk?</h2>
          <p className="ux2-text-body-lg">
            Speak directly to a specialist pension advisor for <strong>free</strong>, friendly guidance on maximising your retirement income with no strings attached.
          </p>
          <p>
            <span>Call free on <a href={v1CallHref}><strong>{v1CallNumber}</strong></a></span>
            <button type="button" className="ux2-text-button" onClick={book}>
              BOOK AN APPOINTMENT
              <i className="bi bi-calendar3" aria-hidden="true"></i>
            </button>
          </p>
        </div>
      </section>

      <QuoteBoostBand
        ariaLabel="Maximise your retirement payout"
        callHref={v1CallHref}
        callNumber={v1CallNumber}
        onBook={book}
      />
      {infoDrawerType && <QuoteExplainerDrawer type={infoDrawerType} onClose={() => setInfoDrawerType(null)} />}
    </section>
  );
}

export function DrawdownBookingStep(props: LanderV2StepProps) {
  return (
    <BookingStepBase
      {...props}
      appointmentType="portal-drawdown-appointment"
      annuityDateField="bookGeneralDrawdownAppointmentDate"
    />
  );
}
